/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormulaContext, FormulaTestEvent } from '@gh/builder-shared';
import { Formula, isFormulaEmpty } from '@gh/builder-shared-data';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { isEmpty } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  getJeItemId,
  isJeItemFromComponent,
  JeComponent,
  JeItem,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  queryJeComponent,
  queryJeEditable,
  queryJeSelectedDetailsTab,
  queryJeSelectedItem,
  queryJeSelectedItemId,
  queryJeSelectionHighlightInvalid,
} from '../je-details.state';

@Component({
  standalone: false,
  selector: 'gh-je-details-cc-item-details',
  templateUrl: 'je-details-cc-item-details.component.html',
  styleUrls: ['je-details-cc-item-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JeDetailsCcItemDetailsComponent implements OnInit, OnDestroy {
  formulaContext: FormulaContext;

  formula$: Observable<Maybe<Formula>>;
  selectionIdentity$: Observable<Maybe<ID>>;
  memo$: Observable<string>;
  total$: Observable<number>;
  selectedTab$: Observable<string>;
  formulaEditable$: Observable<boolean>;
  highlightInvalid$: Observable<boolean>;
  isComponent$: Observable<boolean>;
  isFreeItem$: Observable<boolean>;
  isFormulaEmpty$: Observable<boolean>;
  itemComponent$: Observable<JeComponent>;

  editable$: Observable<boolean>;

  private item: JeItem;
  private itemSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              private state: State<JeDetailsState>,
              private commands: JeCommandService) {

  }

  ngOnInit(): void {
    const { formulaContext } = getRouteInitData(this.route);

    this.formulaContext = formulaContext;

    const { state } = this;
    const item$ = state.select(queryJeSelectedItem).pipe(filter$(Boolean));

    this.formula$ = item$.pipe(map$((item: any) => item.formula));
    this.selectionIdentity$ = state.select(queryJeSelectedItemId);
    this.memo$ = item$.pipe(map$((item: any) => item.memo));
    this.total$ = item$.pipe(map$((item: any) => item.cost || 0));
    this.selectedTab$ = state.select(queryJeSelectedDetailsTab).pipe(filter$(Boolean));
    this.editable$ = state.select(queryJeEditable);
    this.formulaEditable$ = item$.pipe(map$((item: any) => isEmpty(item.cuttingList)));
    this.highlightInvalid$ = state.select(queryJeSelectionHighlightInvalid);
    this.isFormulaEmpty$ = this.formula$.pipe(map$(isFormulaEmpty));
    this.isComponent$ = item$.pipe(map$((item: any) => item && isJeItemFromComponent(item) || false));
    this.isFreeItem$ = item$.pipe(map$((item: any) => item && !isJeItemFromComponent(item) || false));
    this.itemComponent$ = item$.pipe(switchMap$((item: any) =>
      <Observable<JeComponent>>this.state.select(queryJeComponent, <ID>item.jobComponentId)));

    this.itemSubscription = item$.subscribe((item: any) => {
      this.item = item;
    });
  }

  ngOnDestroy(): void {
    this.itemSubscription.unsubscribe();
  }

  onTest(event: FormulaTestEvent): void {
    this.commands.testItemFormula(event, getJeItemId(this.item));
  }

  onFormulaChange(formula: Formula): void {
    const { item } = this;

    if (item) {
      this.commands.costCenterItemSetField(getJeItemId(item), 'formula', formula);
    }
  }

  onMemoChange(memo: string): void {
    const { item } = this;
    if (item) {
      this.commands.costCenterItemSetField(getJeItemId(item), 'memo', memo);
    }
  }

  onSelectedTabChange(name: string): void {
    this.commands.tabSelect(name);
  }

  goToComponent(): void {
    const { item } = this;

    if (item && item.jobComponentId) {
      this.commands.componentSelect(item.jobComponentId);
    }
  }
}
